/**
 * @fileoverview "Start from a template": choose an existing program or class
 * and tick what to bring into the new one.
 *
 * One dialog for both, because the question is the same: which one, and how
 * much of it. Parts marked `client` fill in the form behind this dialog, where
 * they can still be changed before anything is saved; the rest are copied by
 * the server when the new program or class is created
 * (`services/program_templates`), and what could not be copied comes back in
 * words rather than silently.
 *
 * A class template can come from any of the organization's programs, so the
 * caller can offer a program picker (`groups`) above the class list.
 */

import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/studio/primitives";

const selectClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]";

const defaults = (parts) => new Set(parts.filter((p) => p.defaultOn !== false).map((p) => p.id));

/**
 * @param {{
 *   open: boolean, onOpenChange: (open: boolean) => void,
 *   title: string, description?: string,
 *   sourceLabel: string, sources: Array<{id: string, name: string, hint?: string}>, loading?: boolean,
 *   groupLabel?: string, groups?: Array<{id: string, name: string}>, groupValue?: string,
 *   onGroupChange?: (id: string) => void,
 *   parts: Array<{id: string, label: string, desc?: string, defaultOn?: boolean}>,
 *   initialSource?: string, initialParts?: string[],
 *   onApply: (choice: {source: object, parts: string[]}) => void,
 *   testId?: string,
 * }} props
 */
export default function TemplatePickerDialog({
  open,
  onOpenChange,
  title,
  description,
  sourceLabel,
  sources,
  loading = false,
  groupLabel,
  groups,
  groupValue,
  onGroupChange,
  parts,
  initialSource,
  initialParts,
  onApply,
  testId = "template-picker",
}) {
  const [sourceId, setSourceId] = useState("");
  const [checked, setChecked] = useState(() => defaults(parts));

  /* Each opening starts from what was chosen last (when changing a template)
     or from the defaults. */
  useEffect(() => {
    if (!open) return;
    setSourceId(initialSource || "");
    setChecked(initialParts ? new Set(initialParts) : defaults(parts));
  }, [open, initialSource, initialParts, parts]);

  /* The list may arrive after the dialog, or change with the program picker:
     keep a choice that is still offered, else take the first. */
  useEffect(() => {
    if (!open) return;
    setSourceId((current) => (sources.some((s) => s.id === current) ? current : sources[0]?.id || ""));
  }, [open, sources]);

  const source = useMemo(() => sources.find((s) => s.id === sourceId) || null, [sources, sourceId]);
  const allOn = checked.size === parts.length;

  const toggle = (id) =>
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const apply = () => {
    if (!source || checked.size === 0) return;
    onApply({ source, parts: parts.map((p) => p.id).filter((id) => checked.has(id)) });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[560px]" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">{title}</DialogTitle>
          {description && (
            <DialogDescription className="font-sans text-[14px] text-[var(--text-secondary)]">{description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {groups && (
            <label className="block">
              <span className="mb-1.5 block font-sans text-[13px] font-medium text-[var(--text-tertiary)]">{groupLabel}</span>
              <select
                value={groupValue || ""}
                onChange={(e) => onGroupChange?.(e.target.value)}
                aria-label={groupLabel}
                data-testid={`${testId}-group`}
                className={selectClass}
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="block">
            <span className="mb-1.5 block font-sans text-[13px] font-medium text-[var(--text-tertiary)]">{sourceLabel}</span>
            {loading ? (
              <p className="font-sans text-[14px] text-[var(--text-tertiary)]">Loading…</p>
            ) : sources.length === 0 ? (
              <p className="font-sans text-[14px] text-[var(--text-tertiary)]" data-testid={`${testId}-empty`}>
                Nothing to copy from yet.
              </p>
            ) : (
              <select
                value={sourceId}
                onChange={(e) => setSourceId(e.target.value)}
                aria-label={sourceLabel}
                data-testid={`${testId}-source`}
                className={selectClass}
              >
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.hint ? `${s.name} · ${s.hint}` : s.name}
                  </option>
                ))}
              </select>
            )}
          </label>

          <fieldset>
            <div className="mb-2 flex items-center justify-between gap-3">
              <legend className="font-sans text-[13px] font-medium text-[var(--text-tertiary)]">What to copy</legend>
              <button
                type="button"
                onClick={() => setChecked(allOn ? new Set() : new Set(parts.map((p) => p.id)))}
                data-testid={`${testId}-all`}
                className="font-sans text-[13px] font-medium text-[var(--accent-yellow-text)] hover:underline"
              >
                {allOn ? "Clear all" : "Select all"}
              </button>
            </div>
            <div className="flex flex-col gap-2.5">
              {parts.map((p) => (
                <label key={p.id} className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={checked.has(p.id)}
                    onChange={() => toggle(p.id)}
                    data-testid={`${testId}-part-${p.id}`}
                    className="mt-1 accent-[var(--accent-yellow)]"
                  />
                  <span className="min-w-0">
                    <span className="block font-sans text-[15px] text-[var(--text-primary)]">{p.label}</span>
                    {p.desc && <span className="block font-sans text-[13px] text-[var(--text-tertiary)]">{p.desc}</span>}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Kept in view while a long list of parts scrolls under it. */}
          <div className="sticky -bottom-6 -mx-6 -mb-6 flex flex-wrap items-center justify-end gap-3 border-t border-[var(--studio-divider)] bg-background px-6 py-4">
            <Button variant="bare" onPress={() => onOpenChange(false)} data-testid={`${testId}-cancel`}>
              Cancel
            </Button>
            <Button onPress={apply} disabled={!source || checked.size === 0} data-testid={`${testId}-apply`}>
              Use this template
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The parts a server copies, from a picker's choice: the ones not marked
 * `client`, which only ever fill in the form.
 */
export function serverParts(parts, chosen) {
  const client = new Set(parts.filter((p) => p.client).map((p) => p.id));
  return chosen.filter((id) => !client.has(id));
}

/** A copy's outcome in one line for a toast: what was left out, and why. */
export function skippedLine(summary) {
  const skipped = summary?.skipped || [];
  return skipped.length ? skipped.map((s) => s.reason).join(" · ") : undefined;
}
